import { faChevronDown, faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import useApi from 'helpers/api';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import useNotification from 'hooks/useNotification';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Dropdown, Form, Modal, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import PauseReasonsService from 'services/PauseReasons.service';

const TicketAppointmentRegistrationModal = ({
  showModal,
  handleClose,
  ticketId,
  refreshTicket,
  refreshList,
  refreshLogs
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const api = useApi();
  const form = useRef(null);
  const navigate = useNavigate();
  const { authenticatedUser } = useAuthenticatedUser();

  const editorRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [agents, setAgents] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [filters] = useState({ includeInactives: false });

  const getTodayDatetime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10); // Format as YYYY-MM-DD for date input
  };

  const initialState = {
    description: '',
    typeService: 'INTERNAL',
    timeSpent: '',
    startTime: '',
    endTime: '',
    date: getTodayDatetime(),
    pauseSla: false,
    pauseReason: { id: '' },
    user: { id: authenticatedUser?.id || '' }
  };

  const [body, setBody] = useState(initialState);

  useEffect(() => {
    getPauseReasons();
    getAgents();
  }, []);

  useEffect(() => {
    if (showModal) {
      clearFields();
    }
  }, [showModal, ticketId]);

  useEffect(() => {
    if (showModal && authenticatedUser) {
      setBody(prevBody => ({
        ...prevBody,
        user: { id: authenticatedUser?.id }
      }));
    }
  }, [showModal, authenticatedUser]);

  const getPauseReasons = async () => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await PauseReasonsService.list(queryParams);

    response.content && setPauseReasons(response.content);
  };

  const getAgents = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 1000,
        inactive: false
      }).toString();
      const response = await api.agents.list(queryParams);
      if (response.content) {
        setAgents(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar agentes:', error);
    }
  };

  const validateForm = e => {
    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
      return false;
    }
    return true;
  };

  const prepareBody = () => {
    const bodyToSend = { ...body };
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    bodyToSend.date = `${bodyToSend.date}T${hours}:${minutes}`;

    // Garantir que user seja sempre um objeto { id: number }
    if (bodyToSend.user && typeof bodyToSend.user === 'number') {
      bodyToSend.user = { id: bodyToSend.user };
    } else if (bodyToSend.user && !bodyToSend.user.id && bodyToSend.user) {
      bodyToSend.user = { id: bodyToSend.user };
    }

    return bodyToSend;
  };

  const create = async e => {
    if (!validateForm(e)) return;
    setIsLoading(true);
    const bodyToSend = prepareBody();
    const response = await api.appointments.create(ticketId, bodyToSend);
    if (response.id) {
      showSuccessNotification('Apontamento criado com sucesso');
      refreshLogs();
      refreshList();
      handleClose();
    } else {
      showErrorNotification(response.userMessage);
    }
    setIsLoading(false);
  };

  const createAndPause = async e => {
    if (!validateForm(e)) return;
    setIsLoading(true);
    const bodyToSend = prepareBody();
    const response = await api.appointments.createAndPause(
      ticketId,
      bodyToSend
    );
    if (response.id) {
      showSuccessNotification('Apontamento criado com sucesso');
      refreshTicket();
      handleClose();
    } else {
      showErrorNotification(response.userMessage);
    }
    setIsLoading(false);
  };

  const clearFields = () => {
    setBody(initialState);
  };

  const getCurrentDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10); // Format as YYYY-MM-DD for date input max
  };

  const today = getCurrentDate();

  const calculateTimeSpent = (start, end) => {
    if (!start || !end) return '';

    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);

    if (
      isNaN(startHours) ||
      isNaN(startMinutes) ||
      isNaN(endHours) ||
      isNaN(endMinutes)
    ) {
      return '';
    }

    let totalMinutes =
      endHours * 60 + endMinutes - (startHours * 60 + startMinutes);

    if (totalMinutes < 0) {
      totalMinutes += 24 * 60; // Handle next day
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}`;
  };

  const getTimeSpentStyle = () => {
    if (!(body.startTime && body.endTime)) return {};

    const isDark =
      document.documentElement.getAttribute('data-bs-theme') === 'dark';

    if (isDark) {
      return {
        backgroundColor: 'rgba(0, 210, 122, 0.2)',
        borderColor: 'rgba(0, 210, 122, 0.4)',
        color: '#00d27a'
      };
    }

    return {
      backgroundColor: '#d4edda',
      borderColor: '#c3e6cb'
    };
  };

  const setFieldBody = (fieldName, value) => {
    setBody(prevBody => {
      const newBody = { ...prevBody };

      // Tratar campo user separadamente - sempre manter como objeto { id: number }
      if (fieldName === 'user') {
        newBody.user = { id: value };
      } else {
        newBody[fieldName] = value;
      }

      // Se startTime ou endTime foram alterados, calcular timeSpent quando ambos estiverem preenchidos
      if (fieldName === 'startTime' || fieldName === 'endTime') {
        const start = fieldName === 'startTime' ? value : newBody.startTime;
        const end = fieldName === 'endTime' ? value : newBody.endTime;

        if (start && end) {
          const calculatedTime = calculateTimeSpent(start, end);
          if (calculatedTime) {
            newBody.timeSpent = calculatedTime;
          }
        }
      }

      return newBody;
    });
  };

  return (
    <Modal size="lg" show={showModal} onHide={handleClose}>
      {isLoading && <Loading />}
      <Modal.Header closeButton>
        <Modal.Title>Novo apontamento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="appointment.type">
              <Form.Label>
                Tipo de Apontamento <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Tipo"
                value={body.typeService}
                onChange={e => setFieldBody('typeService', e.target.value)}
                required
              >
                <option value="INTERNAL" key="INTERNAL">
                  Interno
                </option>
                <option value="EXTERNAL" key="EXTERNAL">
                  Externo
                </option>
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Escolha uma opção!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="appointment.agent">
              <Form.Label>
                Agente <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Agente"
                value={body.user.id}
                onChange={e => setFieldBody('user', Number(e.target.value))}
                required
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.firstName} {agent.lastName}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="3" controlId="appointment.date">
              <Form.Label>
                Data <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={body.date}
                onChange={e => setFieldBody('date', e.target.value)}
                required
                max={today}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Selecione uma data!
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" controlId="appointment.startTime">
              <Form.Label>Hora Início</Form.Label>
              <Form.Control
                type="time"
                value={body.startTime}
                onChange={e => setFieldBody('startTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="3" controlId="appointment.endTime">
              <Form.Label>Hora Fim</Form.Label>
              <Form.Control
                type="time"
                value={body.endTime}
                onChange={e => setFieldBody('endTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="3" controlId="appointment.timeSpent">
              <Form.Label>
                Tempo <span className="text-danger">*</span>
              </Form.Label>
              <MaskedInput
                mask={[/[0-9]/, /\d/, ':', /\d/, /\d/]}
                placeholder="HH:MM"
                required
                guide={false}
                value={body.timeSpent}
                onChange={e => setFieldBody('timeSpent', e.target.value)}
                readOnly={!!(body.startTime && body.endTime)}
                style={getTimeSpentStyle ? getTimeSpentStyle() : {}}
                render={(ref, props) => <Form.Control ref={ref} {...props} />}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira o tempo trabalhado!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={2}>
              <Form.Label>Pausar SLA</Form.Label>
              <Form.Switch
                value={body.pauseSla}
                onChange={() => setFieldBody('pauseSla', !body.pauseSla)}
              />
            </Form.Group>

            {body.pauseSla && (
              <Form.Group as={Col} md={4}>
                <Form.Label>
                  Motivo de pausa <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  required={body.pauseReason}
                  value={body.pauseReason.id}
                  onChange={e =>
                    setBody({ ...body, pauseReason: { id: e.target.value } })
                  }
                >
                  <option value="">Selecione</option>
                  {pauseReasons.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Motivo de pausa obrigatório.
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Row>

          <Form.Group controlId="appointment.profile">
            <Form.Label className="d-flex align-items-center justify-content-between">
              <span>
                Descrição
                <span className="text-danger"> *</span>
              </span>
              <MessageTemplateSelector
                type="APPOINTMENT"
                ticketId={ticketId}
                editorRef={editorRef}
                onTemplateApplied={content => {
                  setFieldBody('description', content);
                }}
              />
            </Form.Label>

            <Editor
              data={body.description}
              ref={editorRef}
              value={value => setFieldBody('description', value)}
            />
            <MessageTemplateAutocomplete
              editorRef={editorRef}
              type="APPOINTMENT"
              ticketId={ticketId}
              onTemplateApplied={content => {
                setFieldBody('description', content);
              }}
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira uma descrição!
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Fechar
        </Button>
        <div className="position-relative d-flex">
          <Button
            style={{ borderRadius: '40px 0 0 40px' }}
            variant="primary"
            type="submit"
            className="m-0"
            onClick={create}
          >
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Salvar
          </Button>
          <Dropdown drop="up" className="d-flex m-0" style={{ height: '38px' }}>
            <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>

            <Dropdown.Menu
              className="position-absolute w-100"
              style={{ transform: 'translateX(-65%)' }}
            >
              <Dropdown.Item onClick={createAndPause}>
                Salvar e Pausar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const CustomToggle = React.forwardRef(({ onClick }, ref) => (
  <Button
    style={{
      borderRadius: '0 5px 5px 0',
      height: '38px',
      padding: '6px 12px'
    }}
    ref={ref}
    className="m-0 d-flex align-items-center justify-content-center"
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faChevronDown} />
  </Button>
));

export default TicketAppointmentRegistrationModal;
